import { useState } from "react";
import { Link } from "react-router-dom";
import type { OverdueJob } from "../../api/workflow";
import { formatDate } from "../../api/dates";
import { Card, Pill } from "../../components/ui";
import { daysOver, ownerLabel } from "./labels";

/** Every late job, worst first, filtered by whose court it is in. Each row is
 * a link to that job's journey, so the selection is an address and survives a
 * reload. The bar is how late against the worst, so the list reads at a
 * glance without comparing numbers. */
export function OverdueCard({
  rows,
  owners,
  selectedId,
}: {
  rows: OverdueJob[];
  /** Every owner the stages name, in stage order, so the pills do not move
   * as jobs come and go. */
  owners: string[];
  selectedId: number | null;
}) {
  const [owner, setOwner] = useState<string | null>(null);
  const shown = owner === null ? rows : rows.filter((row) => row.owner === owner);
  const worst = Math.max(1, ...rows.map((row) => row.days_over));
  const countFor = (o: string) => rows.filter((row) => row.owner === o).length;
  return (
    <Card
      className="overdue-card"
      title="Overdue"
      sub="worst first"
      flush
      actions={
        <div className="pill-row" role="group" aria-label="Filter by owner">
          <Pill on={owner === null} onToggle={() => setOwner(null)} count={rows.length}>
            All
          </Pill>
          {owners.map((o) => (
            <Pill key={o} on={owner === o} onToggle={() => setOwner(owner === o ? null : o)} count={countFor(o)}>
              {ownerLabel(o)}
            </Pill>
          ))}
        </div>
      }
    >
      {shown.length === 0 ? (
        <p className="empty-state">
          {rows.length === 0 ? "Nothing is overdue." : `Nothing overdue with ${ownerLabel(owner ?? "")}.`}
        </p>
      ) : (
        <ul className="overdue-list" aria-label="Overdue jobs">
          {shown.map((row) => {
            const over = daysOver(row.days_over);
            const selected = row.site.id === selectedId;
            return (
              <li key={`${row.site.id}-${row.stage}`}>
                <Link
                  to={`/workflow/sites/${row.site.id}`}
                  className={selected ? "overdue-row selected" : "overdue-row"}
                  aria-current={selected ? "true" : undefined}
                >
                  <span className="overdue-what">
                    <span className="overdue-site">{row.site.name}</span>
                    <span className="overdue-meta">
                      {row.stage} · {ownerLabel(row.owner)} · due {formatDate(row.due)}
                    </span>
                  </span>
                  <span className="overdue-bar" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, (row.days_over / worst) * 100)}%` }} />
                  </span>
                  <span className="overdue-days">
                    {over} day{over === 1 ? "" : "s"} over
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
